import React, { useContext, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { MenuItem, ListItemIcon, ListItemText, Grid } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MuiDialog from '../../SystemDesign/MuiDialog';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import { UPDATE_REQUIREMENT } from '../../Reducers/Actions';
import { useUpdateSkillPosteRequirementMutation } from '../../services/SkillsManagement';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import FormInput from '../../SystemDesign/FormInput';
import FormSelect from '../../SystemDesign/FormSelect';

// Map level number <-> label
const levelToLabel = (lvl) => {
  const map = { 1: 'Awareness', 2: 'Skilled Application', 3: 'Management' };
  const n = Number(lvl);
  return map[n] || lvl;
};

const normalizeLevelToNumber = (val) => {
  const n = Number(val);
  if (!Number.isNaN(n) && n >= 1 && n <= 3) return n;
  const s = String(val || '').toLowerCase();
  if (s.includes('awareness')) return 1;
  if (s.includes('skilled')) return 2;
  if (s.includes('management')) return 3;
  return 1;
};

const UpdateRequirementSkills = ({ row, onUpdated }) => {
  const dispatch = useDispatch();
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  const [updateRequirement] = useUpdateSkillPosteRequirementMutation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const req = row?.original || row || {};
  const requirementId = req?.id ?? req?.skill_poste_id;

  const initialValues = useMemo(() => ({
    required_level: normalizeLevelToNumber(req?.required_level ?? 1),
    min_score: Number(req?.min_score ?? 1),
    max_score: Number(req?.max_score ?? 3),
  }), [req]);

  const validationSchema = yup.object().shape({
    required_level: yup.number().oneOf([1, 2, 3]).required('Required level is required'),
    min_score: yup.number().required('Min score is required'),
    max_score: yup.number().required('Max score is required').test('gte', 'Max score must be >= Min score', function (value) {
      return Number(value) >= Number(this.parent.min_score);
    }),
  });

  // Field configuration (mirrors UpdateEmployee dynamic form rendering)
  const formFields = useMemo(() => ([
    {
      name: 'required_level',
      label: 'Required Level',
      type: 'select',
      required: true,
      options: [
        { id: 1, title: '1 - Awareness' },
        { id: 2, title: '2 - Skilled Application' },
        { id: 3, title: '3 - Management' },
      ],
    },
    { name: 'min_score', label: 'Min Score', type: 'number', required: true },
    { name: 'max_score', label: 'Max Score', type: 'number', required: true },
  ]), []);

  function getFieldGridProps(field) {
    switch (field.name) {
      case 'required_level':
        return { xs: 12, md: 12, lg: 6 };
      case 'min_score':
      case 'max_score':
        return { xs: 12, md: 6, lg: 3 };
      default:
        return { xs: 12 };
    }
  }

  const handleChange = (e, formik) => {
    const { name, value } = e.target;
    let next = value;
    if (name === 'required_level' || name === 'min_score' || name === 'max_score') {
      next = Number(value);
    }
    formik.setFieldValue(name, next);
  };

  const handleSubmit = async (values) => {
    try {
      const resp = await updateRequirement({
        skillPosteId: requirementId,
        body: {
          // backend accepts label (as seen during create), send label for consistency
          required_level: levelToLabel(values.required_level),
          min_score: Number(values.min_score),
          max_score: Number(values.max_score),
        },
      }).unwrap();

      // Prefer backend response if present, else fall back to local merge
      const serverItem = resp?.requirement || resp?.data || resp;
      const updatedForStore = serverItem && (serverItem.id || serverItem.skill_poste_id)
        ? {
            ...req,
            ...serverItem,
            id: serverItem.skill_poste_id ?? serverItem.id ?? requirementId,
          }
        : {
            ...req,
            id: requirementId,
            required_level: Number(values.required_level),
            min_score: Number(values.min_score),
            max_score: Number(values.max_score),
            updatedAt: new Date().toISOString(),
          };

      // Update Redux immediately (like UpdatePoste pattern)
      dispatch({ type: UPDATE_REQUIREMENT, payload: updatedForStore });

      setInfoAlert({ severity: 'success', message: 'Requirement updated successfully' });
      setOpenSnackBar(true);
      setDialogOpen(false);
      return true;
    } catch (error) {
      console.error('Failed to update requirement:', error);
      const msg = error?.data?.message || error?.message || 'An error occurred while updating requirement';
      setInfoAlert({ severity: 'error', message: msg });
      setOpenSnackBar(true);
      return false;
    }
  };

  const triggerButton = (
    <MenuItem onClick={() => setDialogOpen(true)}>
      <ListItemIcon>
        <EditIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Edit</ListItemText>
    </MenuItem>
  );

  const dialogContent = (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <Form id="updateRequirementForm" autoComplete="off">
          <Grid container spacing={2} marginTop={2}>
            {formFields.map((field, index) => (
              <Grid item size={{ ...getFieldGridProps(field) }} key={index}>
                {field.type === 'number' ? (
                  <FormInput
                    type={field.type}
                    name={field.name}
                    label={field.label}
                    required={field.required}
                    value={formik.values[field.name]}
                    onChange={(e) => handleChange(e, formik)}
                    onBlur={formik.handleBlur}
                    error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                    helperText={formik.touched[field.name] && formik.errors[field.name]}
                    InputLabelProps={{ shrink: true }}
                  />
                ) : (
                  <FormSelect
                    name={field.name}
                    label={field.label}
                    options={field.options}
                    required={field.required}
                    value={formik.values[field.name]}
                    onChange={(e) => handleChange(e, formik)}
                    onBlur={formik.handleBlur}
                    error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                    helperText={formik.touched[field.name] && formik.errors[field.name]}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </Form>
      )}
    </Formik>
  );

  // If no requirement id, render nothing to keep hooks order intact
  if (!requirementId) return null;

  return (
    <>
      {triggerButton}

      <MuiDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        icon={<EditIcon />}
        title="Update Requirement"
        subtitle={`Update requirement for ${req?.skill?.name || 'skill'}`}
        content={dialogContent}
        existAction={true}
        actionTitle="Update Requirement"
        actionType="submit"
        formId="updateRequirementForm"
      />
    </>
  );
};

UpdateRequirementSkills.menuIcon = <EditIcon fontSize="small" />;
UpdateRequirementSkills.menuLabel = 'Edit';

export default UpdateRequirementSkills;

